/* =====================================================================
   CART DRAWER PREMIUM — custom element
   - <cart-drawer-premium> owns open/close, scroll-lock, focus, qty,
     remove, fetch-driven re-render via /cart/change.js?sections=...
   - <cart-drawer> is registered as a hidden PROXY so legacy
     product-form.js (`document.querySelector('cart-drawer')`) still
     finds an element. Proxy forwards getSectionsToRender() and
     renderContents() to the real <cart-drawer-premium>.
   ===================================================================== */

(function () {
  if (window.__cartDrawerPremiumLoaded) return;
  window.__cartDrawerPremiumLoaded = true;

  const SECTION_ID = 'cart-drawer-premium';
  const ICON_SECTION_ID = 'cart-icon-bubble';

  function getDrawer() {
    return document.querySelector('cart-drawer-premium');
  }

  /* ===================================================================
     <cart-drawer-premium>
     =================================================================== */

  class CartDrawerPremium extends HTMLElement {
    constructor() {
      super();
      this._onKeydown = this._onKeydown.bind(this);
      this._lastFocus = null;
    }

    connectedCallback() {
      this._bind();
      this._setupTrigger();
    }

    _bind() {
      this.addEventListener('click', (e) => {
        const t = e.target;
        if (t.closest('[data-cdp-close]') || t.closest('[data-cdp-scrim]')) {
          e.preventDefault();
          this.close();
          return;
        }
        const minus = t.closest('[data-cdp-qty="minus"]');
        const plus = t.closest('[data-cdp-qty="plus"]');
        const remove = t.closest('[data-cdp-remove]');
        if (minus) { e.preventDefault(); this._stepQty(minus, -1); return; }
        if (plus) { e.preventDefault(); this._stepQty(plus, 1); return; }
        if (remove) { e.preventDefault(); this._removeLine(remove); return; }
      });

      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cdp-qty-input]');
        if (!input) return;
        const item = input.closest('[data-cdp-item]');
        if (!item) return;
        const line = parseInt(item.dataset.cdpLine, 10);
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        this._updateQty(line, qty, item);
      });
    }

    _setupTrigger() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.__cdpBound) return;
      cartLink.__cdpBound = true;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (e) => {
        if (e.code === 'Space' || e.code === 'Enter') {
          e.preventDefault();
          this.open(cartLink);
        }
      });
    }

    /* ---------- open / close ---------- */

    open(trigger) {
      if (trigger) this._lastFocus = trigger;
      else this._lastFocus = document.activeElement;

      document.body.classList.add('cdp-no-scroll');
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this._onKeydown);

      requestAnimationFrame(() => {
        const panel = this.querySelector('[data-cdp-panel]');
        if (panel) panel.focus({ preventScroll: true });
      });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cdp-no-scroll');
      document.removeEventListener('keydown', this._onKeydown);
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        try { this._lastFocus.focus(); } catch (e) { /* noop */ }
      }
    }

    _onKeydown(e) {
      if (e.code === 'Escape') {
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.code === 'Tab') {
        this._trapFocus(e);
      }
    }

    _trapFocus(e) {
      const focusables = this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------- quantity / remove ---------- */

    _stepQty(button, delta) {
      const item = button.closest('[data-cdp-item]');
      if (!item) return;
      const input = item.querySelector('[data-cdp-qty-input]');
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + delta);
      input.value = next;
      const line = parseInt(item.dataset.cdpLine, 10);
      this._updateQty(line, next, item);
    }

    _removeLine(button) {
      const item = button.closest('[data-cdp-item]');
      if (!item) return;
      const line = parseInt(item.dataset.cdpLine, 10);
      this._updateQty(line, 0, item);
    }

    _updateQty(line, quantity, itemEl) {
      if (itemEl) itemEl.classList.add('is-loading');
      const errEl = this.querySelector('[data-cdp-errors]');
      if (errEl) errEl.textContent = '';

      fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          line,
          quantity,
          sections: [SECTION_ID, ICON_SECTION_ID],
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((state) => {
          if (state.status) {
            if (errEl) errEl.textContent = state.message || state.description || 'Update failed';
            if (itemEl) itemEl.classList.remove('is-loading');
            return;
          }
          this._applySections(state);
          if (window.PUB_SUB_EVENTS && typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-premium', cartData: state });
          }
        })
        .catch(() => {
          if (errEl) errEl.textContent = 'Network error. Please try again.';
          if (itemEl) itemEl.classList.remove('is-loading');
        });
    }

    _applySections(state) {
      const sections = state && state.sections;
      if (!sections) return;

      // Replace drawer body using cart-drawer-premium section
      const drawerHTML = sections[SECTION_ID];
      if (drawerHTML) {
        const parsed = new DOMParser().parseFromString(drawerHTML, 'text/html');
        const fresh = parsed.querySelector('cart-drawer-premium');
        if (fresh) {
          // preserve open state
          const wasOpen = this.classList.contains('is-open');
          this.innerHTML = fresh.innerHTML;
          this.className = fresh.className + (wasOpen ? ' is-open' : '');
          this.dataset.cartCount = fresh.dataset.cartCount || '0';
        }
      }

      // Update cart icon bubble
      const iconHTML = sections[ICON_SECTION_ID];
      if (iconHTML) {
        const parsed = new DOMParser().parseFromString(iconHTML, 'text/html');
        const fresh = parsed.querySelector('#cart-icon-bubble') || parsed.querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (fresh && target) {
          target.innerHTML = fresh.innerHTML;
          // re-bind trigger on the new node tree if needed
          target.__cdpBound = false;
          this._setupTrigger();
        }
      }
    }

    /* ---------- product-form.js compatibility ---------- */

    // Called by product-form.js right before /cart/add.js — it appends
    // the returned section IDs to the request, then calls renderContents.
    getSectionsToRender() {
      return [
        { id: SECTION_ID, selector: 'cart-drawer-premium' },
        { id: ICON_SECTION_ID, selector: '#cart-icon-bubble' },
      ];
    }

    // Called by product-form.js with the JSON response of /cart/add.js
    renderContents(parsedState) {
      this._applySections(parsedState);
      // Open the drawer after add-to-cart
      requestAnimationFrame(() => this.open());
    }

    // product-form.js calls this to remember focus
    setActiveElement(el) { this._lastFocus = el; }
  }

  customElements.define('cart-drawer-premium', CartDrawerPremium);

  /* ===================================================================
     <cart-drawer> — hidden legacy proxy
     Forwards renderContents() and getSectionsToRender() to the real
     premium drawer so product-form.js works without modification.
     =================================================================== */

  class CartDrawerProxy extends HTMLElement {
    getSectionsToRender() {
      const real = getDrawer();
      return real ? real.getSectionsToRender() : [];
    }
    renderContents(state) {
      const real = getDrawer();
      if (real) real.renderContents(state);
    }
    setActiveElement(el) {
      const real = getDrawer();
      if (real) real.setActiveElement(el);
    }
    open(trigger) {
      const real = getDrawer();
      if (real) real.open(trigger);
    }
    close() {
      const real = getDrawer();
      if (real) real.close();
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawerProxy);
  }
})();
